import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Fade,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Login as LoginIcon, Visibility, VisibilityOff } from '@mui/icons-material';

const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const Login = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
    if (rateLimitInfo) setRateLimitInfo(null);
  };

  // eslint-disable-next-line no-unused-vars
  const handleRefreshToken = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    try {
      console.log('Login: Sending refresh token request');
      const response = await api.post('/api/auth/refresh-token', { refreshToken });
      const { accessToken } = response.data;
      if (!accessToken) {
        throw new Error('No access token received from refresh');
      }
      localStorage.setItem('accessToken', accessToken);
      console.log('Login: New access token received');
      return accessToken;
    } catch (err) {
      console.error('Login: Refresh token error:', err.response?.data || err.message);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!formData.email || !isValidEmail(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      console.log('Login: Starting login process for:', formData.email.trim().toLowerCase());
      
      // Step 1: Login request
      const response = await api.post('/api/auth/login', {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      });
      
      const { accessToken, refreshToken } = response.data;
      
      if (!accessToken) {
        throw new Error('No access token received from login');
      }

      console.log('Login: Tokens received successfully');
      
      // Step 2: Store tokens
      localStorage.setItem('accessToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      // Step 3: For rate limiting, skip immediate user verification and let AuthProvider handle it
      if (onLoginSuccess) {
        console.log('Login: Triggering auth context update');
        try {
          const updatedUser = await onLoginSuccess();
          if (updatedUser) {
            // Successfully updated auth context, navigate based on role
            const targetRoute = updatedUser.role === 'superadmin' ? '/superadmin' : '/dashboard';
            console.log('Login: Navigating to:', targetRoute);
            navigate(targetRoute, { replace: true });
            return;
          }
        } catch (authErr) {
          console.log('Login: Auth context update failed:', authErr);
          // If rate limited (429), don't fail the login completely
          if (authErr.response?.status === 429) {
            console.log('Login: Rate limited during auth check, navigating to dashboard anyway');
            navigate('/dashboard', { replace: true });
            return;
          }
        }
      }

      // Fallback: Try direct verification only if auth context update failed for non-rate-limit reasons
      try {
        console.log('Login: Fallback - Direct user verification');
        const userResponse = await api.get('/api/auth/user');
        console.log('Login: User verified:', userResponse.data.email, userResponse.data.role);
        
        if (!userResponse.data.role) {
          throw new Error('User role not defined');
        }

        const targetRoute = userResponse.data.role === 'superadmin' ? '/superadmin' : '/dashboard';
        console.log('Login: Navigating to:', targetRoute);
        navigate(targetRoute, { replace: true });

      } catch (userErr) {
        console.log('Login: Direct verification failed:', userErr);
        
        // For rate limiting, navigate to dashboard anyway since we have valid tokens
        if (userErr.response?.status === 429) {
          console.log('Login: Rate limited, but tokens are valid. Navigating to dashboard.');
          navigate('/dashboard', { replace: true });
          return;
        }
        
        // For 401 errors, try token refresh
        if (userErr.response?.status === 401) {
          try {
            await handleRefreshToken();
            console.log('Login: Token refreshed, navigating to dashboard');
            navigate('/dashboard', { replace: true });
          } catch (refreshErr) {
            console.error('Login: Token refresh failed:', refreshErr);
            throw new Error('Session expired. Please log in again.');
          }
        } else {
          throw new Error(userErr.response?.data?.msg || 'Failed to fetch user data');
        }
      }

    } catch (err) {
      console.error('Login: Login process failed:', err.response?.data || err.message);
      let errorMsg = 'Login failed. Please check your credentials and try again.';
      
      if (err.response?.status === 429) {
        errorMsg = 'Too many login attempts from this IP. Please wait 15 minutes before trying again.';
        setRateLimitInfo({
          resetTime: Date.now() + (15 * 60 * 1000), // 15 minutes from now
          message: 'Rate limit will reset in 15 minutes'
        });
      } else if (err.response?.status === 423) {
        errorMsg = 'Your account is temporarily locked. Please contact support.';
      } else if (err.response?.status === 401) {
        errorMsg = 'Invalid email or password. Please check your credentials.';
      } else if (err.response?.data?.msg) {
        errorMsg = err.response.data.msg;
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
      
      // Clean up on error
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail || !isValidEmail(forgotPasswordEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');
    setForgotPasswordMessage('');

    try {
      await api.post('/api/auth/forgot-password', {
        email: forgotPasswordEmail.trim().toLowerCase(),
      });
      setForgotPasswordMessage('Password reset instructions have been sent to your email.');
      setForgotPasswordEmail('');
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.response?.data?.msg || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={6} sx={{ padding: 4, width: '100%', position: 'relative', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <LoginIcon sx={{ fontSize: 40, color: 'primary.main', mb: 2 }} />
            <Typography component="h1" variant="h5" sx={{ fontWeight: 'bold' }}>
              Traders Journal
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Sign in to your account
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mt: 2, wordBreak: 'break-word' }}
              action={
                error.includes('15 minutes') ? (
                  <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                    Try using a different network or wait for the limit to reset.
                  </Typography>
                ) : null
              }
            >
              {error}
            </Alert>
          )}

          {rateLimitInfo && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {rateLimitInfo.message}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                You can try again after: {new Date(rateLimitInfo.resetTime).toLocaleTimeString()}
              </Typography>
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              type="email"
              error={!!error && error.includes('email')}
              helperText={error && error.includes('email') ? error : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              error={!!error && error.includes('Password')}
              helperText={error && error.includes('Password') ? error : 'Minimum 6 characters'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5, borderRadius: 1, textTransform: 'none', fontSize: '1rem' }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' }, mb: 1 }}
                onClick={() => setShowForgotPassword(!showForgotPassword)}
              >
                Forgot Password?
              </Typography>
              <Link to="/register" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                  Don't have an account? Sign Up
                </Typography>
              </Link>
            </Box>

            {/* Forgot Password Section */}
            {showForgotPassword && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Reset Password
                </Typography>
                {forgotPasswordMessage && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    {forgotPasswordMessage}
                  </Alert>
                )}
                <Box component="form" onSubmit={handleForgotPassword}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="forgotPasswordEmail"
                    label="Email Address"
                    name="forgotPasswordEmail"
                    autoComplete="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    disabled={loading}
                    type="email"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2, mb: 1, py: 1.5, borderRadius: 1, textTransform: 'none' }}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Send Reset Email'}
                  </Button>
                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                      setForgotPasswordMessage('');
                      setError('');
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          {loading && (
            <Fade in={loading} timeout={500}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  zIndex: 1000,
                  borderRadius: 2,
                }}
              >
                <CircularProgress size={40} sx={{ color: 'primary.main' }} />
                <Typography variant="body1" sx={{ mt: 2, color: 'white' }}>
                  Signing in...
                </Typography>
              </Box>
            </Fade>
          )}
        </Paper>

        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
          'Invest your money in foreign trade, and one of these days you will make a profit.' Ecclesiastes 11:1
        </Typography>
      </Box>
    </Container>
  );
};

export default Login;
